// frontend/src/pages/BookListPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { fetchBooks, fetchBookCategories } from '../services/bookService';
import BookCard from '../components/library/BookCard';
import FilterPanel from '../components/library/FilterPanel';

// نقشه اسلاگ تگ‌ها به عنوان فارسی
const TAG_TITLE_MAP = {
    'Author': 'تألیفات',
    'Translation': 'ترجمه‌ها',
    'Introduction': 'مقدمه‌ها',
    'Editor': 'ویراسته‌ها',
    'mostafa-malekian': 'تمام آثار مصطفی ملکیان',
    'Spinoza': 'کتاب‌های مرتبط با اسپینوزا',
    'Stoics': 'رواقیان',
    'Stoicism': 'رواقی‌گری',
};

// تابع ساخت عنوان داینامیک بر اساس فیلترها
const getDynamicTitle = (filters, apiTitle = null) => {
    if (filters.search) {
        const searchTerm = decodeURIComponent(filters.search);
        return `لیست کامل جستجوی "${searchTerm}"`;
    }
    if (filters.author__name) {
        const authorName = decodeURIComponent(filters.author__name);
        return `لیست کامل آثار نویسنده "${authorName}"`;
    }
    if (filters.translator__name) {
        const translatorName = decodeURIComponent(filters.translator__name);
        return `لیست کامل ترجمه‌های "${translatorName}"`;
    }
    if (filters.publisher__title) {
        const publisherName = decodeURIComponent(filters.publisher__title);
        return `لیست کامل نشر "${publisherName}"`;
    }
    if (filters.tags__slug) {
        const tagSlug = filters.tags__slug;
        const mapped = TAG_TITLE_MAP[tagSlug];
        if (mapped) return `لیست کامل ${mapped}`;
        const cleaned = tagSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `لیست کامل ${cleaned}`;
    }
    if (filters.category__slug) {
        if (apiTitle) return `لیست کامل ${apiTitle}`;
        const cleaned = filters.category__slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `لیست کامل ${cleaned}`;
    }
    return 'لیست کامل کتاب‌ها';
};

// رنگ‌های پالت برای زیردسته‌ها (مطابق با رنگ‌های گرید دسته‌بندی اصلی)
const SUBCOLOR_PALETTE = [
    '#10b981', // emerald
    '#14b8a6', // teal
    '#0891b2', // cyan
    '#0ea5e9', // sky
    '#3b82f6', // blue
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#a855f7', // purple
    '#d946ef', // fuchsia
];

const BookListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [books, setBooks] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loadingSub, setLoadingSub] = useState(false);
    const [pageTitleInfo, setPageTitleInfo] = useState({
        title: 'لیست کامل کتاب‌ها',
        categoryTitle: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const currentFilters = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

    const setFilter = useCallback((filters) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value.trim()) newParams.set(key, value);
                else newParams.delete(key);
            });
            return newParams;
        });
    }, [setSearchParams]);

    const clearFilter = useCallback((key) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (key) newParams.delete(key);
            else {
                Array.from(newParams.keys()).forEach(k => {
                    if (k !== 'ordering') newParams.delete(k);
                });
            }
            return newParams;
        });
    }, [setSearchParams]);

    // بارگذاری زیردسته‌ها هنگام تغییر دسته‌بندی
    useEffect(() => {
        const loadSubcategories = async () => {
            const categorySlug = currentFilters['category__slug'];
            if (!categorySlug) {
                setSubcategories([]);
                return;
            }
            setLoadingSub(true);
            try {
                const response = await fetchBookCategories();
                let allCategories = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];
                const currentCategory = allCategories.find(cat => cat.slug === categorySlug);
                if (currentCategory && currentCategory.id) {
                    const subs = allCategories.filter(cat => cat.parent === currentCategory.id);
                    setSubcategories(subs);
                } else {
                    setSubcategories([]);
                }
            } catch (err) {
                console.error('Error loading subcategories:', err);
                setSubcategories([]);
            } finally {
                setLoadingSub(false);
            }
        };
        loadSubcategories();
    }, [currentFilters['category__slug']]);

    // بارگذاری کتاب‌ها (با تجمیع کتاب‌های زیردسته‌ها)
    const loadBooks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const categorySlug = currentFilters['category__slug'];
            let allBooks = [];

            if (categorySlug && subcategories.length > 0) {
                const parentPromise = fetchBooks(currentFilters);
                const subPromises = subcategories.map(sub =>
                    fetchBooks({ ...currentFilters, category__slug: sub.slug })
                );
                const allResponses = await Promise.all([parentPromise, ...subPromises]);
                allBooks = allResponses.flatMap(res => res.data?.results || []);
                const uniqueBooks = Array.from(new Map(allBooks.map(b => [b.id, b])).values());
                setBooks(uniqueBooks);
            } else {
                const response = await fetchBooks(currentFilters);
                setBooks(response.data?.results || []);
            }

            let apiTitle = null;
            if (categorySlug) {
                try {
                    const parentResponse = await fetchBooks(currentFilters);
                    apiTitle = parentResponse.data?.title;
                } catch (e) { /* ignore */ }
            }
            const dynamicTitle = getDynamicTitle(currentFilters, apiTitle);
            setPageTitleInfo({
                title: dynamicTitle,
                categoryTitle: apiTitle || currentFilters.category__slug || null
            });
        } catch (err) {
            console.error("خطا در واکشی لیست کتاب‌ها:", err);
            setError(err.message || "خطای نامشخص در واکشی داده.");
            setBooks([]);
        } finally {
            setLoading(false);
        }
    }, [currentFilters, subcategories]);

    useEffect(() => {
        loadBooks();
    }, [loadBooks]);

    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
    const cleanTitle = pageTitleInfo.title;
    const metaTitle = `${cleanTitle} | فرزانه`;
    const metaDescription = `مشاهده و جستجوی ${cleanTitle} در وب‌اپلیکیشن فرزانه. شامل آثار مصطفی ملکیان و سایر نویسندگان در حوزه فلسفه، روانشناسی و هنر زندگی.`;

    if (loading) {
        return (
            <div className="flex justify-center items-center p-20 min-h-[50vh]">
                <Loader2 size={40} className="animate-spin text-indigo-600" />
            </div>
        );
    }

    const renderMessage = (message) => (
        <div className="bg-white p-10 rounded-xl shadow-lg border border-red-300 rtl text-right">
            <p className="text-[11px] font-thin text-red-600">{message}</p>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={defaultOgImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={defaultOgImage} />
            </Helmet>

            <div className="p-2 md:p-4 min-h-screen rtl text-right">
                <div className="max-w-7xl mx-auto">
                    {/* فیلتر در بالای صفحه */}
                    <div className="mb-6">
                        <FilterPanel
                            currentFilters={currentFilters}
                            setFilter={setFilter}
                            clearFilter={clearFilter}
                            categoryTitle={pageTitleInfo.categoryTitle}
                        />
                    </div>

                    {/* عنوان صفحه */}
                    <h1 className="text-[14px] text-gray-600 font-black mb-6 border-b pb-2">
                        {pageTitleInfo.title}
                    </h1>

                    {/* زیردسته‌ها */}
                    {subcategories.length > 0 && (
                        <div className="mb-8">
                            <div className="flex flex-wrap gap-1">
                                {subcategories.slice(0, 4).map((sub, idx) => {
                                    const color = SUBCOLOR_PALETTE[idx % SUBCOLOR_PALETTE.length];
                                    return (
                                        <Link
                                            key={sub.slug}
                                            to={`/books?category__slug=${sub.slug}`}
                                            className="px-2 py-2 rounded-lg border text-[11px] font-thin transition-colors hover:bg-gray-50"
                                            style={{
                                                borderColor: color,
                                                color: color,
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            {sub.name || sub.title}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* لیست کتاب‌ها */}
                    {error ? (
                        renderMessage(`خطا در بارگذاری: ${error}`)
                    ) : books.length === 0 ? (
                        renderMessage("متأسفانه کتابی با این فیلترها یافت نشد.")
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {books.map((book) => (
                                <BookCard key={book.slug} book={book} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookListPage;